import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { clsx, SEMANTIC_COLORS, SIZES, withSpacing } from '@wds/utils';

import { useTheme } from 'react-jss';

import useStyles from './useStyles';

export const buttonTypes = Object.freeze(['button', 'reset', 'submit']);
export const variants = Object.freeze(['solid', 'ghost', 'text']);

const Button = ({
  ariaPressed,
  buttonType,
  children,
  color,
  disabled,
  href,
  label,
  size,
  stretched,
  variant,
  className,
  ...rest
}) => {
  const [pressed, setPressed] = useState(ariaPressed);
  useEffect(() => {
    if (ariaPressed !== pressed) setPressed(!pressed);
  }, [ariaPressed, pressed]);

  const numberOfChildren = React.Children.count(children);
  const firstChildType = React.Children.toArray(children)[0].type;
  const lastChildType = React.Children.toArray(children)[numberOfChildren - 1]
    .type;

  const iconOnly =
    numberOfChildren === 1 &&
    firstChildType &&
    firstChildType.displayName &&
    firstChildType.displayName.includes('WithIcon');

  const leftIcon =
    numberOfChildren > 1 &&
    firstChildType &&
    firstChildType.displayName &&
    firstChildType.displayName.includes('WithIcon');

  const rightIcon =
    numberOfChildren > 1 &&
    lastChildType &&
    lastChildType.displayName &&
    lastChildType.displayName.includes('WithIcon');

  // https://mathiasbynens.github.io/rel-noopener/
  const autoAddRel = rest.target && rest.target === '_blank' && !rest.rel;
  const rel = autoAddRel && 'noopener noreferrer';

  const theme = useTheme();
  const classes = useStyles({
    color,
    variant,
    size,
    pressed,
    stretched,
    theme,
    iconOnly,
    leftIcon,
    rightIcon,
  });

  const classNames = clsx(className, classes.wdsButton);

  /* eslint-disable react/button-has-type */
  return (
    <Choose>
      <When condition={href}>
        <a {...rest} className={classNames} href={href} title={label} rel={rel}>
          {children}
        </a>
      </When>
      <Otherwise>
        <button
          {...rest}
          className={classNames}
          type={buttonType}
          disabled={disabled}
          aria-pressed={ariaPressed}
          aria-label={label}
        >
          {children}
        </button>
      </Otherwise>
    </Choose>
  );
};

Button.defaultProps = {
  ariaPressed: false,
  buttonType: 'button',
  className: undefined,
  color: 'primary',
  disabled: false,
  href: undefined,
  label: undefined,
  size: 'md',
  stretched: false,
  variant: 'solid',
};

Button.propTypes = {
  ariaPressed: PropTypes.bool,
  buttonType: PropTypes.oneOf(buttonTypes),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  color: PropTypes.oneOf(SEMANTIC_COLORS),
  disabled: PropTypes.bool,
  href: PropTypes.string,
  // eslint-disable-next-line consistent-return
  label: (props, propName) => {
    if (props[propName] === undefined && typeof props.children === 'object') {
      return new Error(
        'A label is required when the child of a Button is a React component'
      );
    }
  },
  size: PropTypes.oneOf(SIZES),
  stretched: PropTypes.bool,
  variant: PropTypes.oneOf(variants),
};

export default withSpacing(Button);
